import {
	type BaseQueryFn,
	type FetchArgs,
	fetchBaseQuery,
	type FetchBaseQueryError,
	type FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query";
import { getAccessToken, logout } from "../../../auth0";

const makeBaseQuery = (
	fetchBaseQueryOptions: Parameters<typeof fetchBaseQuery>[0],
) =>
	fetchBaseQuery({
		...fetchBaseQueryOptions,
		prepareHeaders: async (...args) => {
			const [headers] = args;
			if (headers.has("skip")) {
				headers.delete("skip");
				return headers;
			}

			fetchBaseQueryOptions?.prepareHeaders?.(...args);

			try {
				const token = await getAccessToken();
				if (token) {
					headers.set("authorization", `Bearer ${token}`);
				}
			} catch (e) {
				await logout();
			}

			return headers;
		},
	});

export const makeBaseQueryWithUnauthentication = (
	fetchBaseQueryOptions: Parameters<typeof fetchBaseQuery>[0],
): BaseQueryFn<
	string | FetchArgs,
	unknown,
	FetchBaseQueryError,
	{},
	FetchBaseQueryMeta
> => {
	const baseQuery = makeBaseQuery(fetchBaseQueryOptions);

	return async (args, api, extraOptions) => {
		const result = await baseQuery(args, api, extraOptions);
		if (result.error && result.error.status === 401) {
			await logout();
		}
		return result;
	};
};

/** Never logs out or throws on failure; skips the request entirely when unconfigured (no `baseUrl`). */
export const makeResilientBaseQuery = (
	fetchBaseQueryOptions: Parameters<typeof fetchBaseQuery>[0],
): BaseQueryFn<
	string | FetchArgs,
	unknown,
	FetchBaseQueryError,
	{},
	FetchBaseQueryMeta
> => {
	const baseQuery = fetchBaseQuery({
		timeout: 3000,
		...fetchBaseQueryOptions,
		prepareHeaders: async (...args) => {
			const [headers] = args;
			await fetchBaseQueryOptions?.prepareHeaders?.(...args);

			try {
				const token = await getAccessToken();
				if (token) {
					headers.set("authorization", `Bearer ${token}`);
				}
			} catch (e) {
				console.error("Failed to attach an access token for", e);
			}

			return headers;
		},
	});

	return async (args, api, extraOptions) => {
		if (!fetchBaseQueryOptions?.baseUrl) {
			return {
				error: {
					status: "CUSTOM_ERROR",
					error: "Not configured for this environment",
				} satisfies FetchBaseQueryError,
			};
		}

		const result = await baseQuery(args, api, extraOptions);
		if (result.error && !import.meta.env.PROD) {
			// Never log args: this base query carries profile-preference bodies.
			console.error("Request failed", api.endpoint, result.error.status);
		}
		return result;
	};
};
